import React, { useState, useMemo } from "react";
import { Dialog, DialogContent } from "@shared/ui/dialog";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Search } from "lucide-react";
import { useAttachContract } from "@features/cx/consumerManagement/hooks";

interface AttachContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAttach: (contract: any) => void;
  availableContracts: any[];
  consumerId: string;
}


export const AttachContractModal: React.FC<AttachContractModalProps> = ({
  isOpen,
  onClose,
  onAttach,
  availableContracts,
  consumerId,
}) => {
  const { mutate: attachContract, isPending } = useAttachContract();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );

  const filteredContracts = useMemo(() => {
    if (!searchTerm) return availableContracts;
    return availableContracts.filter(
      (c: any) =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.id?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, availableContracts]);

  const handleCancel = () => {
    setSearchTerm("");
    setSelectedContractId(null);
    onClose();
  };

  const handleAttach = () => {
    console.log("handleAttach called, selectedContractId:", selectedContractId);
    const contract = availableContracts.find(
      (c: any) => c.id.toString() === selectedContractId,
    );
    console.log("Found contract:", contract);
    if (contract) {
      console.log("Calling attachContract hook...");
      attachContract(
        {
          consumerId,
          payload: { remote_contract_id: contract.id },
        },
        {
          onSuccess: () => {
            console.log("attachContract onSuccess");
            onAttach(contract);
            handleCancel();
          },
          onError: (err) => {
            console.error("attachContract onError:", err);
          }
        }
      );
    } else {
      console.warn("No contract found for ID:", selectedContractId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl bg-white rounded-lg shadow-xl border p-0 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <PageHeader
            title="Attach Contract"
            subtitle="Search and select a contract to attach to this consumer account."
          />
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4">
          <div className="relative flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 border-gray-30 text-sm w-full"
            />
          </div>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {filteredContracts.map((contract) => {
              const isSelected = selectedContractId === contract.id.toString();
              return (
                <div
                  key={contract.id}
                  onClick={() => setSelectedContractId(contract.id.toString())}
                  className={`flex items-center justify-between p-4 rounded-md border cursor-pointer transition-colors ${
                    isSelected
                      ? "border-blue-400 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div>
                    <h4
                      className={`text-sm font-semibold ${
                        isSelected ? "text-blue-700" : "text-gray-900"
                      }`}
                    >
                      {contract.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {contract.id}
                    </p>
                  </div>
                  <div
                    className={`text-sm font-medium ${
                      isSelected ? "text-blue-600" : "text-gray-400"
                    }`}
                  >
                    {isSelected ? "Selected" : "Select"}
                  </div>
                </div>
              );
            })}
            {filteredContracts.length === 0 && (
              <div className="text-center py-6 text-sm text-gray-500">
                No contracts found matching your search.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-3 bg-gray-50 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="px-5 h-9 text-sm text-gray-700 bg-white"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAttach}
            disabled={!selectedContractId || isPending}
            className="px-6 h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
          >
            {isPending ? "Attaching..." : "Attach"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
